/**
 * Tenant/Workspace Switcher Component
 * 
 * Dropdown for switching between tenants and workspaces.
 * Typically placed in the header.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { 
  Building2, 
  ChevronDown, 
  Check,
  Folder,
} from 'lucide-react';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { resolveAdminScope } from '../../lib/adminScope';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import { isSuperAdmin, isSuperAdminBySupabaseUserId } from '../../lib/superAdminService';

// ============================================================================
// Component
// ============================================================================

export function TenantSwitcher({
  compact = false,
  iconOnly = false,
}: {
  compact?: boolean;
  /** Icon-rail sidebar: building icon + tooltip, dropdown opens to the right. */
  iconOnly?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localTenants, setLocalTenants] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [userWorkspaceIds, setUserWorkspaceIds] = useState<Set<string>>(new Set());
  const [isSuperAdminUser, setIsSuperAdminUser] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const tenants = useTenantStore(state => state.tenants);
  const workspaces = useTenantStore(state => state.workspaces);
  const currentTenantId = useTenantStore(state => state.currentTenantId);
  const currentWorkspaceId = useTenantStore(state => state.currentWorkspaceId);
  const switchTenant = useTenantStore(state => state.switchTenant);
  const setCurrentWorkspace = useTenantStore(state => state.setCurrentWorkspace);
  const hasFetched = useTenantStore(state => state.hasFetched);
  const fetchTenants = useTenantStore(state => state.fetchTenants);
  const fetchWorkspaces = useTenantStore(state => state.fetchWorkspaces);
  
  const currentRole = useUserStore(state => state.currentRole);
  const assignedRoleNames = useUserStore(state => state.assignedRoleNames);
  const userId = useUserStore(state => state.userId);

  const effectiveAdminScope = useMemo(
    () => resolveAdminScope(currentRole, assignedRoleNames),
    [currentRole, assignedRoleNames],
  );
  const isPlatformAdminEffective = effectiveAdminScope === 'platform' || isSuperAdminUser;
  const isWorkspaceAdminEffective = effectiveAdminScope === 'workspace' && !isSuperAdminUser;
  
  // Merge store tenants with locally fetched tenants
  const allTenants = localTenants.length > 0 ? localTenants : tenants;
  
  const currentTenant = allTenants.find(t => t.id === currentTenantId);
  const currentWorkspace = workspaces.find(w => w.id === currentWorkspaceId);
  // Workspace admin sees only workspaces they are a member of; tenant/platform admin see all for the tenant
  const visibleWorkspaces =
    isWorkspaceAdminEffective
      ? workspaces.filter((w) => userWorkspaceIds.has(w.id))
      : workspaces;

  // super_admins may lack a platform-admin role_assignment; resolve from DB before permissions load.
  useEffect(() => {
    if (!userId || userId === 'unknown') {
      setIsSuperAdminUser(false);
      return;
    }
    let cancelled = false;
    isSuperAdminBySupabaseUserId(userId)
      .then((ok) => { if (!cancelled) setIsSuperAdminUser(ok); })
      .catch(() => { if (!cancelled) setIsSuperAdminUser(false); });
    return () => { cancelled = true; };
  }, [userId]);

  // Fetch tenants on mount for platform admins or if store is empty
  useEffect(() => {
    let cancelled = false;
    async function loadTenants() {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;
      
      setIsLoading(true);
      try {
        if (isPlatformAdminEffective) {
          // Platform admin sees all tenants
          const { data } = await supabase
            .from('tenants')
            .select('id, name, slug')
            .order('name');
          
          if (!cancelled && data && data.length > 0) {
            setLocalTenants(data);
            // Auto-select first tenant if none selected
            if (!currentTenantId) {
              switchTenant(data[0].id, { keepTenantlessWorkspace: true });
            } else if (data.some((t) => t.id === currentTenantId)) {
              fetchWorkspaces(currentTenantId);
            }
          }
        } else if (userId && userId !== 'unknown' && tenants.length === 0) {
          // Run membership fetch and super_admin check in parallel so fallback doesn't add latency
          const [_, isPlatform] = await Promise.all([
            fetchTenants(userId),
            isSuperAdmin(userId).catch(() => false),
          ]);
          const state = useTenantStore.getState();
          if (!cancelled && state.tenants.length === 0 && isPlatform) {
            const { data } = await supabase
              .from('tenants')
              .select('id, name, slug')
              .order('name');
            if (!cancelled && data?.length) {
              setLocalTenants(data);
              if (!state.currentTenantId) switchTenant(data[0].id, { keepTenantlessWorkspace: true });
            }
          }
        }
      } catch (error) {
        if (!cancelled) console.error('[TenantSwitcher] Error loading tenants:', error);
      } finally {
        // Not gated on `cancelled`: another caller's fetchTenants finishing first
        // re-runs this effect, which starts no new load, so nothing else would
        // clear it and the switcher stayed hidden for the whole visit.
        setIsLoading(false);
      }
    }
    
    // Non-admin: run when we have a real userId and haven't fetched yet (skip 'unknown' so we refetch when userId loads).
    // Platform admin: only fetch if neither localTenants nor the store has tenants yet (store may already be populated by Login).
    const storeTenantCount = tenants.length;
    if ((isPlatformAdminEffective && localTenants.length === 0 && storeTenantCount === 0) || (!isPlatformAdminEffective && userId && userId !== 'unknown' && !hasFetched)) {
      loadTenants();
    }
    return () => { cancelled = true; };
  }, [isPlatformAdminEffective, currentRole, assignedRoleNames, userId, tenants.length, hasFetched, localTenants.length, currentTenantId, switchTenant, fetchTenants, fetchWorkspaces]);

  // super_admins table is authoritative; refresh tenant list when detected after a membership-only fetch.
  useEffect(() => {
    if (!isSuperAdminUser || !userId || userId === 'unknown') return;
    void fetchTenants(userId);
  }, [isSuperAdminUser, userId, fetchTenants]);
  
  // Workspace admin: ensure workspaces for current tenant are loaded when we have tenant but no workspaces yet
  useEffect(() => {
    if (isWorkspaceAdminEffective && currentTenantId && userId && workspaces.length === 0) {
      const inTenant = tenants.some((t) => t.id === currentTenantId);
      if (inTenant) fetchWorkspaces(currentTenantId);
    }
  }, [isWorkspaceAdminEffective, currentTenantId, userId, tenants, workspaces.length, fetchWorkspaces]);
  
  // Workspace admin: load which workspaces the user is a member of (so we only show those in the dropdown)
  useEffect(() => {
    if (!isWorkspaceAdminEffective || !userId) {
      setUserWorkspaceIds(new Set());
      return;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('workspace_memberships')
        .select('workspace_id')
        .eq('user_id', userId);
      if (!cancelled && data) setUserWorkspaceIds(new Set(data.map((r) => r.workspace_id)));
    })();
    return () => { cancelled = true; };
  }, [isWorkspaceAdminEffective, userId]);
  
  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Only show when the user can actually do something with it:
  // - platform/workspace admin (can switch workspaces)
  // - multiple tenants available
  // - multiple workspaces available
  const hasSomethingToSwitch =
    isPlatformAdminEffective ||
    isWorkspaceAdminEffective ||
    allTenants.length > 1 ||
    visibleWorkspaces.length > 1;
  if (isLoading || !hasSomethingToSwitch) {
    return null;
  }
  
  const workspaceLabel = currentWorkspace?.name || currentTenant?.name || 'Select Workspace';

  return (
    <div className={`relative ${iconOnly ? '' : 'w-full'}`} ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title={iconOnly ? workspaceLabel : undefined}
        aria-label={workspaceLabel}
        className={
          iconOnly
            ? 'flex h-10 w-10 items-center justify-center  border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
            : `w-full flex items-center gap-2  border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] transition-colors ${compact ? 'px-2 py-1' : 'px-3 py-2'}`
        }
      >
        <Building2
          className={`flex-shrink-0 text-[var(--text-ter)] ${
            iconOnly ? 'h-4 w-4' : compact ? 'h-3 w-3' : 'h-4 w-4'
          }`}
        />
        {!iconOnly && (
          <>
            <div className="text-left min-w-0 flex-1">
              <div
                className={`font-medium text-[var(--text-pri)] truncate ${
                  compact ? 'text-xs max-w-[140px]' : 'text-sm max-w-[150px]'
                }`}
              >
                {workspaceLabel}
              </div>
              {!compact && allTenants.length > 1 && currentTenant && (
                <div className="text-xs text-[var(--text-ter)] truncate max-w-[150px]">
                  {currentTenant.name}
                </div>
              )}
            </div>
            <ChevronDown
              className={`flex-shrink-0 text-[var(--text-ter)] transition-transform ${
                isOpen ? 'rotate-180' : ''
              } ${compact ? 'h-3 w-3' : 'h-4 w-4'}`}
            />
          </>
        )}
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div
          className={`absolute w-64 bg-[var(--bg-raised)] border border-[var(--rule)]  shadow-lg z-50 flex flex-col max-h-[min(520px,80vh)] dark:bg-[var(--bg-raised)] dark:border-[var(--rule)] ${
            iconOnly
              ? 'left-full top-0 ml-2'
              : `left-0 ${compact ? 'bottom-full mb-1' : 'top-full mt-1'}`
          }`}
        >
          {/* Tenants section */}
          {allTenants.length > 0 && (
            <div className="p-2 border-b border-[var(--rule)] overflow-y-auto flex-shrink-0 max-h-72">
              <div className="px-2 py-1 text-xs font-semibold text-[var(--text-ter)] uppercase">
                Organization
              </div>
              {allTenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => {
                    switchTenant(tenant.id);
                    // Stay open so the user can pick a workspace when multiple tenants exist
                    if (allTenants.length === 1) setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-2 py-2  text-left transition-colors ${
                    tenant.id === currentTenantId
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-[var(--surface)] text-[var(--text-sec)]'
                  }`}
                >
                  <Building2 className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 truncate">{tenant.name}</span>
                  {tenant.id === currentTenantId && (
                    <Check className="h-4 w-4 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
          
          {/* Workspaces section: a person's workspaces with no tenant are listed here too */}
          {visibleWorkspaces.length > 0 && (
            <div className="p-2 overflow-y-auto flex-shrink-0 max-h-48">
              <div className="px-2 py-1 text-xs font-semibold text-[var(--text-ter)] uppercase">
                Workspace
              </div>
              {visibleWorkspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => {
                    setCurrentWorkspace(workspace.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-2 py-2  text-left transition-colors ${
                    workspace.id === currentWorkspaceId
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-[var(--surface)] text-[var(--text-sec)]'
                  }`}
                >
                  <Folder className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 truncate">{workspace.name}</span>
                  {workspace.id === currentWorkspaceId && (
                    <Check className="h-4 w-4 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TenantSwitcher;
